import { useMemo } from 'react'

import { Shimmer } from '@wartek-id/shimmer'

import { PerencanaanPegawaiCard } from 'app/KinerjaGpkPage/components/PerencanaanPegawaiCard'
import { PelaksanaanPegawaiCard } from 'app/KinerjaGpkPage/components/PelaksanaanPegawaiCard'
import {
  useAPIKinerja,
  useAPIPelaksanaanProgress,
} from 'api/kinerja/useAPIKinerja'
import { isValidPerencanaanStatus } from 'app/KinerjaGpkPage/utils/perencanaanStatus'
import { PengelolaanKinerjaTimeline } from 'app/KinerjaShared/components/PengelolaanKinerjaTimeline'
import CardPanduan from 'app/KinerjaShared/components/CardPanduan'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'

import type { KinerjaStatusGpk } from 'types/kinerja-gpk'

export function Homepage() {
  // -----------------------------------------
  // Get Perencanaan Data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe: 'GPK',
      }
    )
  const perencanaanStatus: KinerjaStatusGpk | undefined = useMemo(() => {
    const status = perencanaanData?.data?.status
    if (!isValidPerencanaanStatus(status)) {
      return undefined
    }

    return status
  }, [perencanaanData])

  // -----------------------------------------
  // Get Pelaksanaan Progress Data
  // -----------------------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isPelaksanaanProgressLoading,
    isSuccess: isPelaksanaanProgressSuccess,
  } = useAPIPelaksanaanProgress({
    tipe: 'GPK',
    opts: {
      enabled: true,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // -----------------------------------------
  // Derived values
  // -----------------------------------------
  const isLoading = isPerencanaanLoading || isPelaksanaanProgressLoading

  // Pelaksanaan not started yet if its on Diskusi Persiapan (via `state`) phase
  // and the form is not created yet (via `latestFormState`)
  const isPelaksanaanStarted =
    isPelaksanaanProgressSuccess &&
    (pelaksanaanProgressData?.data?.state != 'DOCUMENT_PREPARATION' ||
      pelaksanaanProgressData?.data?.latestFormState != 'NOT_CREATED')

  return (
    <div className="flex w-full min-h-screen">
      <div className="flex-shrink-0 min-h-full">
        <div className="w-64">{/* Sentinel element for sidebar spacing */}</div>
        <KinerjaSidebar activeMenu="gpk-pegawai" sticky />
      </div>
      <div className="w-full gap-4 p-6 space-y-6">
        {isLoading ? (
          <Shimmer className="!h-40 !w-full block" />
        ) : isPelaksanaanStarted ? (
          <PelaksanaanPegawaiCard className="max-w-4xl" />
        ) : (
          <PerencanaanPegawaiCard
            state={perencanaanStatus}
            revision={perencanaanData?.data?.revisi}
          />
        )}

        <PengelolaanKinerjaTimeline
          timeline={[
            {
              title: 'Januari',
              steps: ['Perencanaan Kinerja'],
            },
            {
              title: 'Februari - November',
              steps: [
                'Persiapan Praktik Kinerja',
                'Pelaksanaan Observasi Praktik Kinerja',
                'Pelaksanaan Tindak Lanjut Praktik Kinerja',
              ],
            },
            {
              title: 'Desember',
              steps: ['Penetapan Predikat Kinerja'],
            },
          ]}
        />

        <CardPanduan />
      </div>
    </div>
  )
}
